import { useEffect, useState } from "react";

import Box from "@/components/Box";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import Modal from "@/tpo/Modal";

import {
  getMissingFieldsForPatient,
  getSavePatientEditableFields
} from "../../shared/PatientDetails";
import SelectPatient from "../../shared/ui/SelectPatient";
import SavePatientForm from "../forms/SavePatientForm";
import { DYNAMIC_CMP_PERMISSION_QUERY } from "@/hooks/useDynamicCmpPermission";
import { ORGANISATION_LIST_QUERY } from "@/graphql/organisations/queries/organisation";

export default function GetOrCreatePatient({
  selectedPatient,
  onSelect,
  title,
  description,
  titleProps,
  descriptionProps,
  buttonProps,
  selectPatientFieldProps,
  patientDetailsProps,
  canCreate
}) {
  // NB - selectedPatient is an organisation patient object

  const [showCreatePatientModal, setShowCreatePatientModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);

  const { organisation } = useOrganisationContext();
  const { updateBasketDetails } = useOrganisationBasketContext();

  const patient = organisation?.patients.find(
    p => p.patientProfile.pk === selectedPatient?.patientProfile?.pk
  );

  const [value, setValue] = useState(patient?.id || "");

  useEffect(() => {
    if (value === "" && patient?.id) {
      setValue(patient.id);
    } else if (value && patient?.id && patient.id !== value) {
      setValue(patient.id);
    } else if (value !== "" && patient?.id === undefined) {
      setValue("");
    }
  }, [patient, value]);

  return (
    <>
      <SelectPatient
        canCreate={canCreate}
        selectedPatient={selectedPatient}
        onSelect={onSelect}
        title={title}
        titleProps={titleProps}
        description={description}
        descriptionProps={descriptionProps}
        buttonProps={buttonProps}
        selectPatientFieldProps={selectPatientFieldProps}
        patientDetailsProps={patientDetailsProps}
        value={value}
        setValue={setValue}
        onCreate={() => {
          setShowCreatePatientModal(true);
        }}
        onEdit={() => {
          setShowEditPatientModal(true);
        }}
      />
      <Modal
        maxWidth={1020}
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        bg="white"
        show={showEditPatientModal}
        close={() => setShowEditPatientModal(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
        data-component-name="EditPatientModal"
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          {patient && (
            <SavePatientForm
              key="edit"
              title="Edit Patient Details"
              data-component-name="EditPatientForm"
              editableFields={getSavePatientEditableFields(patient)}
              missingFields={getMissingFieldsForPatient(patient)}
              defaultValues={{
                id: patient.id,
                firstName: patient.patientProfile.user.firstName,
                lastName: patient.patientProfile.user.lastName,
                email: patient.patientProfile.user.email,
                sex: patient.patientProfile.gender,
                dateOfBirth: patient.patientProfile.dateOfBirth || "",
                requiresGuardianConsent: patient.requiresGuardianConsent,
                guardianFirstName: patient.patientProfile.guardian?.user.firstName || "",
                guardianLastName: patient.patientProfile.guardian?.user.lastName || "",
                guardianEmail: patient.patientProfile.guardian?.user.email || ""
              }}
              handleSuccess={({ data }) => {
                setShowEditPatientModal(false);
              }}
            />
          )}
        </Box>
      </Modal>
      <Modal
        maxWidth={1020}
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        bg="white"
        show={showCreatePatientModal}
        close={() => setShowCreatePatientModal(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          <SavePatientForm
            key="create"
            handleSuccess={({ data }) => {
              setValue("");
              updateBasketDetails({
                patient: data.savePatientMutation.patient.patientProfile.pk,
                usePatientAddress: true
              });
              setShowCreatePatientModal(false);
            }}
            mutationOptions={{
              refetchQueries: [
                { query: ORGANISATION_LIST_QUERY },
                { query: DYNAMIC_CMP_PERMISSION_QUERY, variables: { organisationId: organisation.id, permission: "can_create_patient" } }
              ],
            }}
          />
        </Box>
      </Modal>
    </>
  );
}

GetOrCreatePatient.defaultProps = {
  title: "Assign this item to patient",
  description:
    "Once you assign this order to a patient, the kit(s) will automatically be registered to them and can’t be changed."
};
